<template>
  <div class="conversations-page">
    <!-- ============ Left: conversation list ============ -->
    <aside class="conv-list">
      <div class="conv-list__header">
        <h2 class="conv-list__title">Messages</h2>
      </div>

      <div v-if="loadingConversations" class="conv-list__status">Loading conversations…</div>

      <div v-else-if="!conversations.length" class="conv-list__status">
        No conversations yet.
      </div>

      <ul v-else class="conv-list__items">
    <li
        v-for="conv in conversations"
        :key="conv.id"
        class="conv-item"
        :class="{ 'is-active': selectedConversation && selectedConversation.id === conv.id }"
        @click="selectConversation(conv)"
      >
        <div class="conv-item__avatar">{{ initials(otherParticipant(conv).company_name) }}</div>
        <div class="conv-item__body">
          <div class="conv-item__row">
            <span class="conv-item__name">{{ otherParticipant(conv).company_name }}</span>
            <span class="conv-item__time">{{ previewOf(conv).time }}</span>
          </div>
          <span class="conv-item__preview" :class="{ 'conv-item__preview--new': hasUnread(conv.id) }">
            {{ hasUnread(conv.id) ? 'New message' : (previewOf(conv).text || 'No messages yet') }}
          </span>
        </div>
        <span v-if="hasUnread(conv.id)" class="conv-item__badge">
          {{ chatState.unreadByConversation[conv.id] }}
        </span>
      </li>
      </ul>
    </aside>

    <!-- ============ Right: active chat ============ -->
    <section class="conv-chat">
      <div v-if="!selectedConversation" class="conv-chat__empty">
        <span class="conv-chat__empty-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.9-.94L3 20l1.06-3.9A8.4 8.4 0 0 1 3 11.5 8.5 8.5 0 0 1 20.5 8" />
          </svg>
        </span>
        <p>Select a conversation to start chatting.</p>
      </div>

      <template v-else>
       <header class="conv-chat__header">
        <div class="conv-chat__identity">
          <div class="conv-chat__avatar">
            {{ initials(otherParticipant(selectedConversation).company_name) }}
          </div>

          <strong class="conv-chat__name">
            {{ otherParticipant(selectedConversation).company_name }}
          </strong>
        </div>

        <button
          v-if="isLoggedIn && this.selectedConversation.sender_supplier_id !== mySupplierId"
          type="button"
          class="my-supplier-btn"
          :class="`my-supplier-btn--${mySupplierStatus || 'none'}`"
          :disabled="mySupplierButtonDisabled"
          @click="addToMySuppliers"
        >
          <i class="fas" :class="mySupplierButtonIcon"></i>
          {{ mySupplierButtonLabel }}
        </button>
      </header>

        <div ref="messageList" class="conv-chat__messages">
          <p v-if="loadingHistory" class="conv-chat__status">Loading messages…</p>
          <p v-else-if="!messages.length" class="conv-chat__status">
            No messages yet. Say hello to {{ otherParticipant(selectedConversation).company_name }}.
          </p>

          <div v-for="(msg, i) in messages" :key="i" class="chat-msg-row" :class="{ 'chat-msg-row--mine': msg.mine }">
            <div class="chat-msg" :class="{ 'chat-msg--mine': msg.mine }">
              <div v-if="msg.parent" class="chat-msg__quote">
                <span class="chat-msg__quote-text">{{ msg.parent.text || 'Attachment' }}</span>
              </div>
              <p v-if="msg.text" class="chat-msg__text">{{ msg.text }}</p>
              <a
                v-if="msg.attachment"
                :href="msg.attachment.url"
                target="_blank"
                rel="noopener"
                class="chat-msg__attachment"
              >
                📎 {{ msg.attachment.name }}
              </a>
              <span class="chat-msg__time">{{ msg.time }}</span>
            </div>

            <button type="button" class="chat-msg__reply-btn" aria-label="Reply" @click="replyTo(msg)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 17l-5-5 5-5" />
                <path d="M4 12h10a6 6 0 0 1 6 6v1" />
              </svg>
            </button>
          </div>
        </div>

        <div class="conv-chat__composer">
          <div v-if="replyingTo" class="conv-chat__reply-preview">
            <div class="conv-chat__reply-preview-bar"></div>
            <div class="conv-chat__reply-preview-body">
              <span class="conv-chat__reply-preview-label">
                Replying to {{ replyingTo.mine ? 'yourself' : otherParticipant(selectedConversation).company_name }}
              </span>
              <span class="conv-chat__reply-preview-text">{{ replyingTo.text || 'Attachment' }}</span>
            </div>
            <button type="button" class="conv-chat__reply-cancel" aria-label="Cancel reply" @click="cancelReply">✕</button>
          </div>

          <div v-if="pendingFile" class="conv-chat__file-chip">
            {{ pendingFile.name }}
            <button type="button" aria-label="Remove file" @click="pendingFile = null">✕</button>
          </div>

          <div class="conv-chat__input-row">
            <button type="button" class="conv-chat__attach" aria-label="Attach file" @click="$refs.fileInput.click()">
              📎
            </button>
            <input ref="fileInput" type="file" class="conv-chat__file-input" @change="onFileSelected" />
            <textarea
              v-model="draftMessage"
              class="conv-chat__textarea"
              rows="2"
              placeholder="Write a message..."
              :disabled="sending"
              @keydown.enter.exact.prevent="sendMessage"
            ></textarea>
            <button type="button" class="conv-chat__send" :disabled="!canSend" @click="sendMessage">
              {{ sending ? '…' : 'Send' }}
            </button>
          </div>
        </div>
      </template>
    </section>
  </div>
</template>
<script>
import { form_api_submit } from '@/utils/FormSubmit'

import {
  subscribeToConversation,
  unsubscribeFromAll,
  clearUnread,
  clearConversationUnread,
  pollConversation,
  setLastMessageId,
  startGlobalPolling,   // NEW
  stopGlobalPolling,    // NEW
  POLL_INTERVAL_MS,
  chatState,
} from '@/utils/chatStore'

export default {
  name: 'ConversationsPage',

  data() {
   return {
      mySupplierId: null,

      conversations: [],
      loadingConversations: true,

      selectedConversation: null,
      messages: [],
      loadingHistory: false,

      draftMessage: '',
      pendingFile: null,
      sending: false,

      replyingTo: null,

      isLoggedIn: !!localStorage.getItem('supplier_id'),
      mySupplierStatus: null,
      addingSupplier: false,
      pollTimer: null,

      chatState, // expose reactive store to the template
    }
  },

  computed: {
    canSend() {
      return !this.sending && (this.draftMessage.trim().length > 0 || this.pendingFile)
    },
        mySupplierButtonLabel() {
      if (this.addingSupplier) return 'Sending request…'
      if (this.mySupplierStatus === 'approved') return 'My Supplier'
      if (this.mySupplierStatus === 'pending') return 'Request Pending'
      if (this.mySupplierStatus === 'rejected') return 'Request Declined'
      return 'Add to My Suppliers'
    },

    mySupplierButtonIcon() {
      if (this.mySupplierStatus === 'approved') return 'fa-check'
      if (this.mySupplierStatus === 'pending') return 'fa-clock'
      if (this.mySupplierStatus === 'rejected') return 'fa-ban'
      return 'fa-user-plus'
    },

    mySupplierButtonDisabled() {
      return this.addingSupplier || !!this.mySupplierStatus
    },

  },

async mounted() {
  this.mySupplierId = Number(localStorage.getItem('supplier_id'))
  await this.loadConversations()
  this.subscribeToAllConversations()
  this.startPolling()
  startGlobalPolling(
    this.mySupplierId,
    () => this.selectedConversation?.id ?? null,
    (freshRows) => this.mergeConversationList(freshRows) // NEW
  )
},
beforeUnmount() {
  this.stopPolling()
  stopGlobalPolling() // NEW
  chatState.activeConversationId = null
  unsubscribeFromAll()
},
watch: {
  'chatState.latestByConversation': {
    deep: true,
    handler() {
      this.resortConversationsByLatest()
    },
  },
},
  methods: {
      hasUnread(conversationId) {
      return !!chatState.unreadByConversation[conversationId]
    },

    previewOf(conv) {
      // NEW — prefer a live-polled/echo-pushed preview over the initially-loaded one
      const live = chatState.latestByConversation[conv.id]
      const latest = live || conv.latest_message
      if (!latest) return { text: '', time: '' }
      return {
        text: latest.body || (latest.attachment_name ? `📎 ${latest.attachment_name}` : ''),
        time: latest.created_at
          ? new Date(latest.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '',
      }
    },

    getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },

    otherParticipant(conv) {
      const isMineSender = Number(conv.sender_supplier_id) === Number(this.mySupplierId)
      return (isMineSender ? conv.receiver_supplier : conv.sender_supplier) || {}
    },

    initials(name) {
      return (name || '')
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },

    // ---- Loading ----
    async loadConversations() {

      this.loadingConversations = true

      const result = await form_api_submit({
        url: 'chat/conversations',
        method: 'GET',
        data: { sender_id: this.mySupplierId },
        show_success_toast: false,
        show_error_toast: false,
      })
      const rows = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result.data?.data)
          ? result.data.data
          : []

      if (result.success) {
        this.conversations = rows
              this.checkMySupplierStatus()
      }

      this.loadingConversations = false
    },

    // ---- Realtime ----
    subscribeToAllConversations() {
      this.conversations.forEach((conv) => {
        subscribeToConversation(conv.id, this.mySupplierId, (eventMessage, isMine) => {
          this.handleIncomingMessage(conv.id, eventMessage, isMine)
        })
      })
    },

  // ---- Realtime ----
    handleIncomingMessage(conversationId, eventMessage, isMine) {
      const idx = this.conversations.findIndex((c) => c.id === conversationId)
      if (idx > -1) {
        const [conv] = this.conversations.splice(idx, 1)
        conv.latest_message = {
          body: eventMessage.body,
          attachment_name: eventMessage.attachment_name,
          sender_supplier_id: eventMessage.sender_supplier_id,
          created_at: eventMessage.created_at || new Date().toISOString(),
        }
        this.conversations.unshift(conv)
      }

      const isOpen = this.selectedConversation && this.selectedConversation.id === conversationId
      if (isOpen && !isMine) {
        const alreadyExists = eventMessage.id && this.messages.some((m) => m.id === eventMessage.id)
        if (!alreadyExists) {
          this.messages.push(this.mapSingleMessage(eventMessage))
          this.$nextTick(this.scrollMessagesToEnd)
        }
        // Open conversation — never show "New message" for the one you're already looking at
        clearConversationUnread(conversationId)
      }
      // Non-active conversations: bumpConversationUnread() is already called inside
      // subscribeToConversation's echo listener in chatStore.js, so nothing extra needed here.
    },


    mapSingleMessage(msg) {
      const myId = Number(this.mySupplierId)
      return {
        id: msg.id,
        mine: Number(msg.sender_supplier_id) === myId,
        text: msg.body || '',
        attachment: msg.attachment_url
          ? { url: this.getFileUrl(msg.attachment_url), name: msg.attachment_name, type: msg.type }
          : null,
        parent: msg.parent
          ? {
              id: msg.parent.id,
              text: msg.parent.body || (msg.parent.attachment_name ? `📎 ${msg.parent.attachment_name}` : ''),
              mine: Number(msg.parent.sender_supplier_id) === myId,
            }
          : null,
        time: msg.created_at
          ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }
    },

    selectConversation(conv) {
      this.selectedConversation = conv
      chatState.activeConversationId = conv.id
      this.draftMessage = ''
      this.pendingFile = null
      this.replyingTo = null

      // Clear both the global unread count AND this conversation's own unread flag
      const unread = chatState.unreadByConversation[conv.id] || 0
      if (unread > 0) {
        clearUnread(unread)
        clearConversationUnread(conv.id)
      }

      this.loadExistingConversation()
    },

    
  async loadExistingConversation() {
    if (!this.mySupplierId || !this.selectedConversation) return

    this.loadingHistory = true
    this.messages = []

    const result = await form_api_submit({
      url: `chat/${this.selectedConversation.id}/messages`,
      method: 'GET',
      data: { sender_id: this.mySupplierId },
      show_success_toast: false,
      show_error_toast: false,
    })

    this.loadingHistory = false
    const rows = Array.isArray(result.data)
      ? result.data
      : Array.isArray(result.data?.data)
        ? result.data.data
        : null

    if (!result.success || !rows) return

    this.messages = this.mapAllConversationMessages(rows, this.mySupplierId)

    if (rows.length) {
      console.log('[ConversationsPage] seeding baseline for', this.selectedConversation.id, '->', rows[rows.length - 1].id)
      setLastMessageId(this.selectedConversation.id, rows[rows.length - 1].id)
    }

    this.$nextTick(this.scrollMessagesToEnd)
  },

  startPolling() {
    this.stopPolling()
    console.log('[ConversationsPage] startPolling every', POLL_INTERVAL_MS, 'ms') // was hardcoded 5min — now matches the store's constant
    this.pollTimer = setInterval(() => {
      this.pollActiveConversation()
    }, POLL_INTERVAL_MS)
  },
  stopPolling() {
    if (this.pollTimer) {
      console.log('[ConversationsPage] stopPolling')
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  },


    async pollActiveConversation() {
      if (!this.selectedConversation) return

      const newRows = await pollConversation(this.selectedConversation.id)
      if (!newRows.length) return

      const mapped = this.mapAllConversationMessages(newRows, this.mySupplierId)
      const existingIds = new Set(this.messages.map((m) => m.id).filter(Boolean))
      const toAppend = mapped.filter((m) => !m.id || !existingIds.has(m.id))

      if (!toAppend.length) return

      this.messages.push(...toAppend)
      this.$nextTick(this.scrollMessagesToEnd)

      // Active conversation stays "read" — make sure no stale unread badge lingers
      clearConversationUnread(this.selectedConversation.id)

      const idx = this.conversations.findIndex((c) => c.id === this.selectedConversation.id)
      if (idx > -1) {
        const [conv] = this.conversations.splice(idx, 1)
        const lastRow = newRows[newRows.length - 1]
        conv.latest_message = {
          body: lastRow.body,
          attachment_name: lastRow.attachment_name,
          sender_supplier_id: lastRow.sender_supplier_id,
          created_at: lastRow.created_at,
        }
        this.conversations.unshift(conv)
      }
    },
    mapAllConversationMessages(messages, mySupplierId) {
      const myId = Number(mySupplierId)
      const sortedMessages = [...messages].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      )

      return sortedMessages.map((msg) => ({
        id: msg.id,
        mine: Number(msg.sender_supplier_id) === myId,
        text: msg.body || '',
        attachment: msg.attachment_url
          ? { url: this.getFileUrl(msg.attachment_url), name: msg.attachment_name, type: msg.type }
          : null,
        parent: msg.parent
          ? {
              id: msg.parent.id,
              text: msg.parent.body || (msg.parent.attachment_name ? `📎 ${msg.parent.attachment_name}` : ''),
              mine: Number(msg.parent.sender_supplier_id) === myId,
            }
          : null,
        time: msg.created_at
          ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '',
      }))
    },

    // ---- Reply ----
    replyTo(msg) {
      this.replyingTo = msg
    },
    cancelReply() {
      this.replyingTo = null
    },

    // ---- Composer ----
    onFileSelected(e) {
      this.pendingFile = e.target.files[0] || null
      e.target.value = ''
    },
resortConversationsByLatest() {
    this.conversations.sort((a, b) => {
      const aLatest = chatState.latestByConversation[a.id]?.created_at || a.latest_message?.created_at || a.created_at
      const bLatest = chatState.latestByConversation[b.id]?.created_at || b.latest_message?.created_at || b.created_at
      return new Date(bLatest) - new Date(aLatest)
    })
  },
   async sendMessage() {
  if (!this.canSend || !this.selectedConversation) return

  const text = this.draftMessage.trim()
  const file = this.pendingFile
  const parent = this.replyingTo

  this.sending = true

  const fd = new FormData()
  fd.append('body', text)
  fd.append('sender_id', this.mySupplierId)
  if (parent?.id) fd.append('parent_id', parent.id)
  if (file) fd.append('attachment', file)

  const result = await form_api_submit({
    url: `chat/${this.selectedConversation.id}/sendmessages`,
    method: 'POST',
    data: fd,
    show_success_toast: false,
    show_error_toast: true,
  })

  if (result.success) {
    const newMsg = result.data?.message || {
      mine: true,
      text,
      attachment: file ? { url: URL.createObjectURL(file), name: file.name } : null,
      parent: parent ? { id: parent.id, text: parent.text, mine: parent.mine } : null,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }

    this.messages.push({ id: result.data?.id, ...newMsg })

    // CRITICAL: mark this message as the new baseline so the next poll
    // doesn't re-fetch (and re-append) the message we just sent ourselves.
    if (result.data?.id) {
      console.log('[ConversationsPage] sendMessage: seeding baseline after send ->', result.data.id)
      setLastMessageId(this.selectedConversation.id, result.data.id)
    }

    this.draftMessage = ''
    this.pendingFile = null
    this.replyingTo = null
    this.$nextTick(this.scrollMessagesToEnd)

    const idx = this.conversations.findIndex((c) => c.id === this.selectedConversation.id)
    if (idx > -1) {
      const [conv] = this.conversations.splice(idx, 1)
      conv.latest_message = {
        body: text,
        attachment_name: file?.name,
        sender_supplier_id: this.mySupplierId,
        created_at: new Date().toISOString(),
      }
      this.conversations.unshift(conv)
    }
  }

  this.sending = false
},

    scrollMessagesToEnd() {
      const list = this.$refs.messageList
      if (list) list.scrollTop = list.scrollHeight
    },
     async checkMySupplierStatus() {
      if (!this.isLoggedIn || !this.selectedConversation) return

      const result = await form_api_submit({
        url: 'my-suppliers/status',
        method: 'GET',
        data: {
          supplier_id: this.mySupplierId,
          target_supplier_id:this.selectedConversation.sender_supplier_id,
        },
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success) {
        this.mySupplierStatus = result.data?.status || null
      }
    },
    async addToMySuppliers() {
      if (!this.isLoggedIn || this.addingSupplier || this.mySupplierStatus) return

      this.addingSupplier = true

      const result = await form_api_submit({
        url: 'my-suppliers/store',
        method: 'POST',
        data: {
          supplier_id: this.mySupplierId,
          target_supplier_id: this.selectedConversation.sender_supplier_id,
        },
        show_success_toast: true,
        show_error_toast: true,
      })

      this.addingSupplier = false

      if (result.success) {
        this.mySupplierStatus = result.data?.status || 'pending'
      }
    },
  mergeConversationList(freshRows) {
    const existingIds = new Set(this.conversations.map((c) => c.id))

    freshRows.forEach((freshConv) => {
      if (!existingIds.has(freshConv.id)) {
        // Brand-new conversation the sidebar has never seen before
        this.conversations.unshift(freshConv)
        subscribeToConversation(freshConv.id, this.mySupplierId, (eventMessage, isMine) => {
          this.handleIncomingMessage(freshConv.id, eventMessage, isMine)
        })
        console.log('[ConversationsPage] new conversation discovered via poll, subscribed', freshConv.id)
      } else {
        // Existing conversation — keep participant/meta fields fresh
        // without clobbering latest_message (chatState already owns that via previewOf()).
        const idx = this.conversations.findIndex((c) => c.id === freshConv.id)
        if (idx > -1) {
          this.conversations[idx] = {
            ...freshConv,
            latest_message: this.conversations[idx].latest_message, // preserve locally-tracked preview
          }
        }
      }
    })

    this.resortConversationsByLatest()
  },
  },
}
</script>

<style scoped>
.conversations-page {
  display: flex;
  background: var(--color-surface);
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.conversations-page * {
  box-sizing: border-box;
}
.conv-list {
  width: 320px;
  flex-shrink: 0;
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.conv-list__header {
  padding: 18px 18px 14px;
  border-bottom: 1px solid var(--color-border);
}
.conv-list__title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: var(--color-text-dark);
}

.conv-list__status {
  padding: 32px 18px;
  text-align: center;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

.conv-list__items {
  list-style: none;
  margin: 0;
  padding: 0;
}

.conv-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: background 0.15s ease;
}
.conv-item:hover {
  background: var(--color-tint-light);
}
.conv-item.is-active {
  background: var(--color-tint-light);
  box-shadow: inset 3px 0 0 var(--color-secondary);
}

.conv-item__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.conv-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.conv-item__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.conv-item__name {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--color-text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conv-item__time {
  flex-shrink: 0;
  font-size: 10.5px;
  color: var(--color-text-muted);
}

.conv-item__preview {
  font-size: 12px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ==========================================================================
   2. Right: chat panel
   ========================================================================== */
.conv-chat {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.conv-chat__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--color-text-muted);
  font-size: 13.5px;
}
.conv-chat__empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--color-tint-light);
  display: grid;
  place-items: center;
  color: var(--color-text-muted);
}
.conv-chat__empty-icon svg {
  width: 22px;
  height: 22px;
}

/* Squeezed, tinted so it doesn't read as a second white top-nav bar */
.conv-chat__header {
  padding: 8px 18px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-tint-light);
  flex-shrink: 0;
}
.conv-chat__identity {
  display: flex;
  align-items: center;
  gap: 10px;
}
.conv-chat__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.conv-chat__name {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.conv-chat__messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #faf8f9;
}

.conv-chat__status {
  text-align: center;
  color: var(--color-text-muted);
  font-size: 13px;
  margin-top: 20px;
}

/* Row wraps a bubble + its reply button so the button can sit just outside it */
.chat-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  align-self: flex-start;
  max-width: 60%;
}
.chat-msg-row--mine {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.chat-msg {
  flex: 1;
  min-width: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px 12px 12px 4px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-dark);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.chat-msg--mine {
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  border-color: transparent;
  border-radius: 12px 12px 4px 12px;
}

.chat-msg__quote {
  border-left: 2px solid rgba(11, 23, 153, 0.35);
  padding: 3px 8px;
  margin-bottom: 6px;
  background: rgba(11, 23, 153, 0.05);
  border-radius: 4px;
}
.chat-msg--mine .chat-msg__quote {
  border-left-color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.14);
}
.chat-msg__quote-text {
  display: block;
  font-size: 11.5px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-msg__text {
  margin: 0 0 4px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.chat-msg__attachment {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 12px;
  color: inherit;
  opacity: 0.9;
}
.chat-msg__time {
  display: block;
  font-size: 10px;
  opacity: 0.65;
  text-align: right;
  margin-top: 2px;
}

.chat-msg__reply-btn {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.chat-msg-row:hover .chat-msg__reply-btn {
  opacity: 1;
}
.chat-msg__reply-btn:hover {
  color: var(--color-secondary);
  border-color: var(--color-secondary);
}
.chat-msg__reply-btn svg {
  width: 13px;
  height: 13px;
}

.conv-chat__composer {
  border-top: 1px solid var(--color-border);
  padding: 14px 22px;
  flex-shrink: 0;
}

.conv-chat__reply-preview {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-bottom: 10px;
  background: var(--color-tint-light);
  border-radius: 8px;
  padding: 8px 10px;
}
.conv-chat__reply-preview-bar {
  width: 3px;
  border-radius: 3px;
  background: var(--color-secondary);
  flex-shrink: 0;
}
.conv-chat__reply-preview-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.conv-chat__reply-preview-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-secondary);
}
.conv-chat__reply-preview-text {
  font-size: 12px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conv-chat__reply-cancel {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: 12px;
}
.conv-chat__reply-cancel:hover {
  color: #b3261e;
}

.conv-chat__file-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  color: var(--color-text-dark);
  margin-bottom: 10px;
}
.conv-chat__file-chip button {
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-text-muted);
  font-size: 12px;
}
.conv-chat__file-chip button:hover {
  color: #b3261e;
}

.conv-chat__input-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.conv-chat__attach {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.conv-chat__file-input {
  display: none;
}

.conv-chat__textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  color: var(--color-text-dark);
  outline: none;
  min-height: 38px;
  max-height: 80px;
  line-height: 1.5;
  transition: border-color 0.15s ease;
}
.conv-chat__textarea:focus {
  border-color: var(--color-secondary);
  box-shadow: 0 0 0 3px rgba(11, 23, 153, 0.08);
}

.conv-chat__send {
  flex-shrink: 0;
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease;
}
.conv-chat__send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.conv-chat__send:active:not(:disabled) {
  transform: scale(0.96);
}
@media (max-width: 860px) {
  .conversations-page {
    flex-direction: column;
    height: auto;
  }
  .conv-list {
    width: 100%;
    max-height: 40vh;
    border-right: none;
    border-bottom: 1px solid var(--color-border);
  }
  .conv-chat {
    min-height: 60vh;
  }
  .chat-msg-row {
    max-width: 82%;
  }
  .chat-msg__reply-btn {
    opacity: 1;
  }
}
.conv-item__badge {
  flex-shrink: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--color-secondary);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.conv-chat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.conv-chat__identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.conv-chat__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.my-supplier-btn {
  flex-shrink: 0;
}
.conv-item__preview--new {
  color: var(--color-secondary);
  font-weight: 700;
}
</style>